import MuiCheckbox from '@mui/material/Checkbox';
import MuiFormControl from '@mui/material/FormControl';
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiFormLabel from '@mui/material/FormLabel';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import { useId } from 'react';

// The label sits ABOVE the control rather than floating into the outline notch, so the field's
// border stays unbroken (the Figma treatment for the address-filter dialog's three fields). Kept
// identical to the TextField wrapper's caption — the two stand in the same form, and a caption a
// step larger on one of them would misalign the pair. Same reason both hardcode size="small".
const ABOVE_LABEL_SX = {
  typography: 'body2',
  // 8px, the grid's own step. The caption sits ABOVE the control rather than on it, so nothing but
  // this margin separates the two, and the longest of these labels is a full sentence — the batch
  // card's input rule — which at 4px read as the field's first line instead of as its caption.
  // Moved in BOTH wrappers at once: the two stand side by side in the address-filter dialog.
  mb: 1,
  // A label ON the control doubles as a focus indicator; one above it is a caption, and the
  // control's own focus ring already says where focus is. Explicit because this one DOES hear about
  // focus — it sits inside the FormControl — and MUI would otherwise tint it primary, leaving the
  // two wrappers' captions behaving differently. `.Mui-error` is deliberately left alone: a red
  // caption over a failed carrier catalog is the field's state, not its focus.
  '&.Mui-focused': { color: 'text.secondary' },
} as const;

// How tall the open list may get. Not a styling preference: MUI's Popover anchors the menu under the
// control, but the moment the list does not fit in what is left of the viewport it pulls it back UP
// — over the field — rather than scrolling it. With ten states opened from the third field of a
// dialog that is the ordinary case, and the control it covers is the only thing naming what is
// marked. A list that always fits is what keeps the shift from ever firing. 224px is MUI's own
// figure for a scrollable menu (4.5 rows of 48px plus the list's 8px padding). Measured in Chrome
// against the filters dialog: the ten states came to a 556px list that started 246px ABOVE the
// field's bottom edge — covering the whole form — and now open flush under it at every viewport
// height from 650px up, four of the ten rows showing plus a partial one, which is what says «this
// scrolls». The five-option «Fecha de entrega» list still fits whole (196px).
const MENU_MAX_HEIGHT = 224;

// The «nothing chosen» copy — the single mode's `placeholder`, the multi mode's `emptyLabel` — is a
// placeholder, so it reads like one instead of like a value the agent picked. `text.secondary` is
// the token `colors.ts` names for precisely this («secondary/placeholder»), and not MUI's own rule
// (currentColor at 0.42 opacity), which is what the TextField wrapper's NATIVE placeholder gets
// from the browser: that one is a pseudo-element no theme can retint, this is real text in a div,
// and a token is what a palette change moves. Over white the two land within a hair of each other
// (#B6B2BD against #ACA5BB). Written on the DISPLAY node rather than on the root so it cannot reach
// the chevron, which is a sibling and says «this opens», not «this is empty».
const EMPTY_DISPLAY_SX = { '& .MuiSelect-select': { color: 'text.secondary' } } as const;

export interface SelectOption {
  value: string;
  label: string;
}

// What both selection modes share. The three that differ — the value, the handler that emits it,
// and the copy for «nothing chosen» — live in the two branches below, because their shapes are
// correlated: an array value has to reach a handler that takes arrays.
interface SelectBaseProps {
  // Visible AND programmatic, drawn above the control. The association runs through
  // `aria-labelledby` and never a `<label for>`: the node carrying role="combobox" is a div, so
  // `for` would reach nothing. Either way it is a real label, which a curated `aria-label` was not.
  label: string;
  options: SelectOption[];
  // Renders the asterisk AND announces the requirement. The two are separate jobs: FormControl
  // buys only the glyph, and a glyph carries no text alternative.
  required?: boolean;
  // Its options are still being fetched. Distinct from `disabled` in meaning and identical in
  // effect: an enabled dropdown holding nothing reads as «this building has no carriers», which
  // is a different fact and one the caller renders differently.
  loading?: boolean;
  disabled?: boolean;
  // The persistent, always-announced companion to the label — for a requirement or a hint that is
  // true whatever the agent does.
  helperText?: string;
  // Something is wrong with the field. Replaces `helperText` rather than joining it: two
  // descriptions on one control is a queue nobody asked for, and the error is the urgent one.
  error?: string;
  // Entering a step of a dialog has to land focus on its first field, and the façade offers no
  // other way in: this wrapper forwards no ref, and a view cannot import MUI to reach the control.
  // MUI focuses the VISIBLE control from its own effect, not the hidden native input.
  autoFocus?: boolean;
  fullWidth?: boolean;
}

export interface SingleSelectProps extends SelectBaseProps {
  // Spelled `false | undefined` rather than left out: under exactOptionalPropertyTypes an omitted
  // property and one explicitly set to undefined are different types, and callers that compute the
  // mode write the second.
  multiple?: false | undefined;
  value: string;
  // Emits the value, not MUI's event — mirroring TextField, so no view handles a synthetic event.
  onChange: (value: string) => void;
  // The «nothing chosen yet» line. A real disabled option rather than MUI's `displayEmpty` text
  // alone, so the empty state is visible in the closed control AND present — and unpickable — in
  // the open list, instead of being a value the agent can select their way back into.
  placeholder?: string;
}

export interface MultiSelectProps extends SelectBaseProps {
  multiple: true;
  value: string[];
  onChange: (values: string[]) => void;
  // The closed control's text when nothing is marked. It draws NO row in the open list — that is
  // what separates it from `placeholder`, which governs both at once. A multi-selection field
  // empties itself by unmarking, so it needs no row to select its way back to.
  emptyLabel: string;
}

export type SelectProps = SingleSelectProps | MultiSelectProps;

export function Select(props: SelectProps) {
  const {
    label,
    options,
    required = false,
    loading = false,
    disabled = false,
    helperText,
    error,
    autoFocus = false,
    fullWidth = false,
  } = props;
  const labelId = useId();
  const describedById = useId();
  const displayId = useId();
  // The error outranks the helper text: one description, and the urgent one wins.
  const description = error ?? helperText;
  // Single-selection only. Read off the discriminant rather than destructured, because the
  // property does not exist on the other branch at all.
  const placeholder = props.multiple === true ? undefined : props.placeholder;
  // Two mutually exclusive sources, and the union is what keeps them from colliding: `placeholder`
  // exists only on the single branch, `emptyLabel` only on the multi one.
  const displayEmpty = placeholder !== undefined || props.multiple === true;
  // «Nothing chosen» in either mode: an empty multi selection, or a single one sitting on its
  // placeholder row. A single select with no placeholder has no such reading — the Carrier field
  // names its default («Todos los carriers») with a real option — so it is never empty here.
  const isEmpty =
    props.multiple === true ? props.value.length === 0 : placeholder !== undefined && props.value === '';
  // Everything the two modes render identically. The element itself is written twice rather than
  // narrowed prop by prop, because the value and the handler that emits it are CORRELATED: an
  // array value has to reach a handler that takes arrays, and one element cannot hold both.
  const shared = {
    labelId,
    // `label` is deliberately NOT passed: it is what makes MUI cut a notch in the outline for a
    // label to sit in, and this one sits above the control instead.
    autoFocus,
    // SelectDisplayProps is the ONLY spread that reaches the visible role="combobox" node.
    // MUI emits aria-required nowhere at all, and routes anything given as inputProps onto a
    // native input it marks aria-hidden with tabIndex -1 — present in the DOM, invisible to
    // assistive tech. Without this the asterisk is the only signal that the field is required.
    SelectDisplayProps: {
      ...(required ? { 'aria-required': true } : {}),
      ...(error !== undefined ? { 'aria-invalid': true } : {}),
      // An id here is what makes MUI point the display node's `aria-labelledby` at ITSELF, so the
      // marked values join the caption in the accessible name. Multi-selection only: a control
      // standing for N values says nothing without it, while the single mode's name is already
      // what its two live callers were built and asserted against.
      ...(props.multiple === true ? { id: displayId } : {}),
    },
    // Unlike the two above, this one HAS a real route: InputBase forwards it to the input
    // component and SelectInput writes it on the display node — which is what makes the
    // description audible at all.
    ...(description !== undefined ? { 'aria-describedby': describedById } : {}),
    ...(displayEmpty ? { displayEmpty: true } : {}),
    // Written on `slotProps.paper` and not on the deprecated `PaperProps`: SelectInput merges this
    // one LAST over the min-width it writes for the anchor's own width, so the cap lands without
    // costing the menu its width.
    MenuProps: { slotProps: { paper: { style: { maxHeight: MENU_MAX_HEIGHT } } } },
    ...(isEmpty ? { sx: EMPTY_DISPLAY_SX } : {}),
  };
  // Direct children of the select, never wrapped: SelectInput clones them to inject `role` and
  // `aria-selected`, and a Fragment in between drops both in silence.
  const rows = [
    ...(placeholder !== undefined
      ? [
          <MuiMenuItem key="" value="" disabled>
            {placeholder}
          </MuiMenuItem>,
        ]
      : []),
    ...options.map((option) => (
      <MuiMenuItem key={option.value} value={option.value}>
        {props.multiple === true && (
          // Paint, not a control. The row already carries the state in `aria-selected`, so a
          // second checked thing inside it would announce the same fact twice — and a control
          // inside an `option` is invalid ARIA to begin with.
          <MuiCheckbox checked={props.value.includes(option.value)} tabIndex={-1} aria-hidden />
        )}
        {option.label}
      </MuiMenuItem>
    )),
  ];
  return (
    // size="small" is hardcoded to match the TextField wrapper: the two sit side by side in the
    // same form and would otherwise stand at different heights.
    <MuiFormControl
      size="small"
      required={required}
      disabled={disabled || loading}
      error={error !== undefined}
      fullWidth={fullWidth}
    >
      {/* FormLabel, not InputLabel: the latter is the FLOATING label — it positions itself
          absolutely over the control and shrinks into the notch. This one is a static caption
          above the field, which is what FormLabel already is. It keeps the id the select points
          `aria-labelledby` at, and the FormControl's `required` is what draws the asterisk. */}
      <MuiFormLabel id={labelId} sx={ABOVE_LABEL_SX}>
        {label}
      </MuiFormLabel>
      {props.multiple === true ? (
        <MuiSelect<string[]>
          {...shared}
          // Not a styling flag: it is what makes MUI keep a SET of marked values, hand the handler
          // the whole array back, and mark each option `aria-selected` instead of just the one.
          multiple
          value={props.value}
          // The closed control speaks the agent's language, so it reads back labels; an unknown
          // value falls through to its own spelling rather than to a gap.
          renderValue={(marked) =>
            marked.length === 0
              ? props.emptyLabel
              : marked.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ')
          }
          onChange={(event) => {
            // MUI widens the payload to the string a NATIVE select would emit on autofill. This
            // control is never native, so what arrives is the array it was handed.
            props.onChange(event.target.value as string[]);
          }}
        >
          {rows}
        </MuiSelect>
      ) : (
        <MuiSelect<string>
          {...shared}
          value={props.value}
          // Emits the value, not MUI's event — mirroring TextField, so no view handles a synthetic
          // event.
          onChange={(event) => {
            props.onChange(event.target.value);
          }}
        >
          {rows}
        </MuiSelect>
      )}
      {description !== undefined && (
        <MuiFormHelperText id={describedById}>{description}</MuiFormHelperText>
      )}
    </MuiFormControl>
  );
}
